import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { PostureChip } from '@/components/ui/posture-chip';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { CitationChip } from '@/components/ui/citation-chip';
import { useAuth } from '@/lib/auth/store';
import { isMsalConfigured, signIn } from '@/lib/auth/msal';
import { useRedeemMagicLinkMutation } from '@/lib/api/auth';

/**
 * Login surface — split hero layout.
 *
 * Left panel (lg+): navy value-proposition hero. Deep Avero-navy field with a
 * slow-drifting token-derived grid, amber + AI-lavender glows, a floating
 * "glass" card that shows a real AI proposal row (built from the actual
 * load-bearing primitives — posture chip, AI confidence chip, citation chip,
 * per CLAUDE.md hard rule #3), and the four product value propositions from
 * `pm-agent/output/productInception.md`.
 *
 * Right panel: the sign-in card. Two auth paths, logic unchanged from the
 * original scaffold:
 *
 *   1. **Entra ID / MSAL** — production users. Redirects to Entra; on return
 *      `initAuth()` (main.tsx) hydrates the session and the effect below
 *      moves the user into the app.
 *   2. **Magic-link redeem** — client-viewers arriving from an email link
 *      paste the token; we POST `/api/v1/auth/magic-link/redeem` and store
 *      the grant id as `viewerGrantId` (sent as `X-Viewer-Grant-Id` on the
 *      chat surface).
 *
 * Plus the dev-admin shortcut for local iteration (seeded firm-root user;
 * only honored by the API when `CALIBER_DEV_AUTH=1`).
 *
 * There is no login screen in `design_handoff_caliber_v2/prototype/` (route
 * inventory in README §4), so this is a net-new composition — built strictly
 * from the token system (README §5) and the §6 load-bearing primitives.
 * Backdrop treatments live in `globals.css` under "Login hero".
 */
export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: z.object({ next: z.string().optional() }),
});

const DEV_ADMIN_USER_ID = '00000000-0000-0000-0000-000000000001';
// Matches the dev JwtVerifier shape from apps/api/src/lib/dev-auth.ts;
// the Fastify host accepts any `Bearer user:<uuid>` token when
// CALIBER_DEV_AUTH=1 is set. Production never satisfies that gate.
const DEV_ADMIN_TOKEN = `user:${DEV_ADMIN_USER_ID}`;
const DEV_ADMIN_EMAIL = 'dev@example.test';

const VALUE_PROPS = [
  {
    n: '01',
    title: 'AI proposes, consultants decide',
    body: 'Methodology, registers, and routed updates arrive as scored proposals. Nothing changes without a consultant confirm or a structured override.',
  },
  {
    n: '02',
    title: 'Every answer cites its source',
    body: 'Ask the engagement anything. Answers are grounded in your document base, cited to title and page, and round-trip to the passage.',
  },
  {
    n: '03',
    title: 'Numbers that trace to evidence',
    body: 'Every KPI, score, and posture carries a source pill back to the SOW, change order, or report it came from. Rollups are computed, never typed.',
  },
  {
    n: '04',
    title: 'Defensible by design',
    body: 'Append-only snapshots, an every-edit audit trail, and engagement-isolated data make every firm-branded PDF audit-ready.',
  },
] as const;

function MicrosoftMark() {
  // Monochrome four-square Microsoft mark; inherits button ink.
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
      <rect x="0" y="0" width="6.4" height="6.4" />
      <rect x="7.6" y="0" width="6.4" height="6.4" />
      <rect x="0" y="7.6" width="6.4" height="6.4" />
      <rect x="7.6" y="7.6" width="6.4" height="6.4" />
    </svg>
  );
}

function BrandBlock({ onNavy = false }: { onNavy?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-card bg-paper-0 shadow-modal">
        <img src="/avero-logo.png" alt="Avero Advisors" className="h-7 w-auto" />
      </span>
      <span className="flex flex-col leading-tight">
        <span
          className={`text-body-sm font-bold tracking-wide ${onNavy ? 'text-paper-0' : 'text-ink-900'}`}
        >
          AVERO ADVISORS
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-amber-500">
          Caliber 2.0
        </span>
      </span>
    </div>
  );
}

/** The hero's floating demo card — a real AI proposal row, real primitives. */
function ProposalDemoCard() {
  return (
    <div className="login-glass-card rounded-large border border-ink-100 p-5 shadow-modal">
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex h-[22px] items-center gap-1.5 rounded-full border border-ai-surface-border bg-ai-surface px-2.5 text-meta font-medium text-ai-ink">
          AI-proposed
        </span>
        <AiConfidenceChip value={0.91} />
      </div>
      <p className="mt-3 text-body-sm text-ink-900">
        Milestone M7 (UAT exit) slipped three weeks in the latest status report — routed to risk
        R-041 with severity raised to High.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <CitationChip
          cite={{
            id: 'demo',
            doc_label: 'Status Report 06-28',
            section: '2.4',
            page: 6,
            confidence: 0.91,
          }}
        />
        <PostureChip status="at-risk" dot />
      </div>
      <div className="mt-4 border-t border-ink-100 pt-3 text-meta text-ink-500">
        Awaiting consultant review — confirm, or override with rationale
      </div>
    </div>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const setSession = useAuth((s) => s.setSession);
  const setViewerGrant = useAuth((s) => s.setViewerGrant);
  const sessionToken = useAuth((s) => s.token);
  const redeem = useRedeemMagicLinkMutation();
  const [token, setToken] = useState('');

  // A returning Entra user lands here with the session already hydrated by
  // initAuth() (MSAL bounces back to the origin, then this route). Move them
  // into the app instead of leaving them staring at the sign-in button. Also
  // covers an already-signed-in user who navigates to /login directly.
  useEffect(() => {
    if (sessionToken) {
      void navigate({ to: (search.next ?? '/') as '/', replace: true });
    }
  }, [sessionToken, navigate, search.next]);

  function handleDevAdmin() {
    setSession(DEV_ADMIN_TOKEN, {
      userId: DEV_ADMIN_USER_ID,
      email: DEV_ADMIN_EMAIL,
      displayName: 'Dev Admin',
    });
    void navigate({ to: (search.next ?? '/') as '/' });
  }

  function handleMicrosoftSignIn() {
    // Redirects to Entra; on return, initAuth() (main.tsx) hydrates the session.
    void signIn();
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) return;
    const result = await redeem.mutateAsync({ token });
    if (result.grant_id) {
      setViewerGrant(result.grant_id);
    }
    void navigate({ to: '/' });
  }

  return (
    <div className="grid min-h-screen bg-paper-50 lg:grid-cols-[1.15fr_1fr]">
      {/* ------------------------------------------------------------------ */}
      {/* Left — value-proposition hero (lg+)                                 */}
      {/* ------------------------------------------------------------------ */}
      <div className="relative hidden overflow-hidden bg-avero-navy lg:flex lg:flex-col">
        {/* Backdrop: drifting grid + token-derived glows */}
        <div className="login-hero-grid pointer-events-none absolute inset-0" aria-hidden="true" />
        <div
          className="login-hero-glow-amber pointer-events-none absolute -left-40 -top-40 h-[560px] w-[560px]"
          aria-hidden="true"
        />
        <div
          className="login-hero-glow-ai pointer-events-none absolute -bottom-48 -right-32 h-[520px] w-[520px]"
          aria-hidden="true"
        />

        <div className="relative z-10 flex flex-1 flex-col px-12 py-8 xl:px-16">
          <BrandBlock onNavy />

          <div className="mt-8 max-w-xl xl:mt-12">
            <p className="text-meta font-semibold uppercase tracking-[0.18em] text-accent-amber-500">
              IV&amp;V Workbench for Government ERP Oversight
            </p>
            <h1 className="mt-3 font-serif text-[34px] font-semibold leading-[1.15] tracking-[-0.01em] text-paper-0 xl:text-[40px]">
              Independent oversight,
              <br />
              grounded in evidence.
            </h1>
            <p className="login-hero-muted mt-4 max-w-lg text-body">
              Contract Compliance, Health Check, and Go-Live Readiness — one AI-driven workbench
              where every proposal is reviewed, every answer is cited, and every report is
              defensible.
            </p>
          </div>

          <div className="mt-6 max-w-md">
            <ProposalDemoCard />
            <p className="login-hero-dim mt-2.5 text-meta">
              Live from the product: every AI claim ships with its source, confidence, and posture.
            </p>
          </div>

          <div className="login-hero-hairline mt-6 grid max-w-2xl grid-cols-2 gap-x-10 gap-y-5 border-t pt-6">
            {VALUE_PROPS.map((vp) => (
              <div key={vp.n}>
                <div className="flex items-baseline gap-2.5">
                  <span className="font-mono text-mono-sm text-accent-amber-500">{vp.n}</span>
                  <h2 className="text-body-sm font-semibold text-paper-0">{vp.title}</h2>
                </div>
                <p className="login-hero-muted mt-1 pl-[30px] text-meta font-normal">{vp.body}</p>
              </div>
            ))}
          </div>

          <div className="login-hero-hairline mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-5">
            <span className="login-hero-dim font-mono text-mono-sm">US data residency</span>
            <span className="login-hero-dim font-mono text-mono-sm">·</span>
            <span className="login-hero-dim font-mono text-mono-sm">StateRAMP-aligned hosting</span>
            <span className="login-hero-dim font-mono text-mono-sm">·</span>
            <span className="login-hero-dim font-mono text-mono-sm">Microsoft Entra ID SSO</span>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Right — sign-in                                                     */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Brand shows here on small screens where the hero is hidden */}
          <div className="mb-8 lg:hidden">
            <BrandBlock />
          </div>

          <h2 className="text-h1 font-semibold text-ink-900">Sign in to Caliber</h2>
          <p className="mt-1.5 text-body-sm text-ink-500">
            The Avero workbench for independent verification &amp; validation.
          </p>

          <div className="mt-6 rounded-large border border-ink-100 bg-paper-0 p-6 shadow-modal">
            {isMsalConfigured() ? (
              <>
                <button
                  type="button"
                  onClick={handleMicrosoftSignIn}
                  className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-card bg-accent-amber-600 text-body-sm font-medium text-paper-0 transition-colors hover:bg-accent-amber-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600 active:bg-accent-amber-700"
                >
                  <MicrosoftMark />
                  Sign in with Microsoft
                </button>
                <p className="mt-2 text-center text-meta font-normal text-ink-500">
                  Avero staff — Entra ID single sign-on
                </p>
                <div className="my-5 flex items-center gap-3">
                  <span className="h-px flex-1 bg-ink-100" />
                  <span className="text-meta text-ink-300">or</span>
                  <span className="h-px flex-1 bg-ink-100" />
                </div>
              </>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label htmlFor="token" className="mb-1.5 block text-meta font-medium text-ink-700">
                  Client viewer — invitation token
                </label>
                <Input
                  id="token"
                  name="token"
                  type="text"
                  autoComplete="one-time-code"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste from your invitation email"
                  className="h-9 rounded-card border-ink-200 bg-paper-0 font-mono text-mono-sm text-ink-900 placeholder:font-sans placeholder:text-body-sm placeholder:text-ink-300"
                  required
                />
                <p className="mt-1.5 text-meta font-normal text-ink-500">
                  Read-only access to your project&apos;s published dashboards and reports.
                </p>
              </div>
              {redeem.error ? <ErrorMessage error={redeem.error} /> : null}
              <button
                type="submit"
                disabled={redeem.isPending || !token}
                className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-card border border-ink-200 bg-paper-0 text-body-sm font-medium text-ink-700 transition-colors hover:border-ink-300 hover:bg-paper-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600 disabled:pointer-events-none disabled:opacity-50"
              >
                {redeem.isPending ? (
                  <>
                    <Spinner /> Redeeming…
                  </>
                ) : (
                  'Continue as client viewer'
                )}
              </button>
            </form>
          </div>

          {/* Dev-only: Vite strips this branch from production builds, so the
              card never renders on the deployed app (the API would reject the
              dev token anyway — CALIBER_DEV_AUTH is never set in prod). */}
          {import.meta.env.DEV ? (
            <div className="mt-4 rounded-card border border-dashed border-ink-200 bg-paper-100 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-body-sm font-semibold text-ink-900">Local development</p>
                  <p className="mt-0.5 text-meta font-normal text-ink-500">
                    Seeded firm-root user; requires CALIBER_DEV_AUTH=1.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleDevAdmin}
                  className="inline-flex h-[30px] shrink-0 items-center rounded-card border border-ink-200 bg-paper-0 px-2.5 text-meta font-medium text-ink-700 transition-colors hover:border-ink-300 hover:bg-paper-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600"
                >
                  Dev admin
                </button>
              </div>
            </div>
          ) : null}

          {/* Compact value props for small screens (hero hidden) */}
          <ul className="mt-8 space-y-2 lg:hidden">
            {VALUE_PROPS.map((vp) => (
              <li key={vp.n} className="flex items-baseline gap-2.5">
                <span className="font-mono text-mono-sm text-accent-amber-600">{vp.n}</span>
                <span className="text-body-sm text-ink-700">{vp.title}</span>
              </li>
            ))}
          </ul>

          <p className="mt-8 text-center text-meta font-normal text-ink-300">
            Avero Advisors · Caliber 2.0 · IV&amp;V Workbench
          </p>
        </div>
      </div>
    </div>
  );
}
